import api from "./api";
import { getApiUrl } from "./config";

export interface DialerSessionLead {
  id?: string;
  leadId?: string;
  name?: string;
  phone?: string;
  phoneNumber?: string;
  fromNumber?: string;
}

export interface DialerSession {
  id: string;
  dialMode: "power_dial" | "preview_dial" | "progressive_dial";
  maxConcurrent: number;
  status: "active" | "paused" | "completed" | "stopped";
  totalLeadsToCall: number;
  completedCalls: number;
  totalCalls: number;
  totalAnswered: number;
  totalFailed: number;
  totalVoicemail: number;
  totalSkipped: number;
  totalDialTime: number;
  outcome: { appointments?: number; underwriting?: number; sold?: number };
  currentLeadIndex: number;
  leadQueue: DialerSessionLead[];
  startedAt: string;
  endedAt: string | null;
  /** 'dialiq' means this session was launched from the DialIQ smart-queue
   *  panel — resume should re-engage predictive mode. Defaults to 'manual'. */
  source?: "manual" | "dialiq";
}

export interface DialerSessionDetail {
  session: DialerSession;
  calls: {
    id: string;
    leadId?: string;
    toNumber: string;
    callResult?: string;
    disposition?: string;
    notes?: string;
    finalLeadStatus?: string;
    duration?: number;
    status: string;
    createdAt: string;
  }[];
}

const BASE = "/communication/dialer-sessions";

export const dialerSessionApi = {
  createSession: (data: { dialMode: "power" | "multiline"; leads: DialerSessionLead[]; source?: "manual" | "dialiq" }) =>
    api.post<{ data: { id: string } }>({ url: getApiUrl(BASE), body: data, auth: true }),

  updateProgress: (id: string, data: Record<string, unknown>) =>
    api.put<{ message: string }>({ url: getApiUrl(`${BASE}/${id}/progress`), body: data, auth: true }),

  completeSession: (id: string, data: Record<string, unknown>) =>
    api.put<{ message: string }>({ url: getApiUrl(`${BASE}/${id}/complete`), body: data, auth: true }),

  saveCallResult: async (id: string, data: Record<string, unknown>) => {
    const response = await api.post<{ message: string; data?: { dialiq?: unknown } }>({
      url: getApiUrl(`${BASE}/${id}/call-result`),
      body: data,
      auth: true,
    });
    // DialIQ: if backend reports a window-advance / terminal event, broadcast it
    // so the global RedialThresholdToast can render without tight coupling here.
    try {
      const payload: any = (response as any)?.data ?? response;
      const dialiq = payload?.dialiq ?? payload?.data?.dialiq;
      if (dialiq && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("dialiq:threshold", {
            detail: {
              leadId: (data as any)?.leadId,
              leadName: (data as any)?.leadName,
              leadState: (data as any)?.leadState,
              ...dialiq,
            },
          })
        );
      }
    } catch {
      /* non-critical — never break the dialer on DialIQ surfacing */
    }
    return response;
  },

  listSessions: (page = 1, limit = 20) =>
    api.get<{ data: { sessions: DialerSession[]; total: number; page: number } }>({
      url: getApiUrl(`${BASE}?page=${page}&limit=${limit}`),
      auth: true,
    }),

  getSession: (id: string) =>
    api.get<{ data: DialerSessionDetail }>({ url: getApiUrl(`${BASE}/${id}`), auth: true }),
};
